import { FC, ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { ThemeProvider, createTheme, useTheme } from '@mui/material'

/**
 * The auth screens run on their own accent, set by the client on 22-9-2026.
 * The console stays burgundy; only the signed-out surface is gold.
 */
export const AUTH_ACCENT = '#927040'
/** Hover / pressed state — the accent at 80%. */
export const AUTH_ACCENT_DARK = '#755A33'

/**
 * Background of the lockup slot on the signed-out screens.
 *
 * There used to be a charcoal disc here, inherited with the Phase 0 copy of
 * the legacy frontend: `bloom.png` is a white wordmark on transparent, so it
 * needed something dark to stand on. The current mark is the app's own gold
 * tile, which brings its own background — so the slot behind it is nothing
 * at all.
 */
export const AUTH_LOGO_DISC = 'transparent'

/**
 * Scopes the accent to whatever it wraps, so the `color="primary"` buttons and
 * links already on these screens follow it without each one being rewritten.
 */
const AuthTheme: FC<{ children?: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  const scoped = createTheme(theme, {
    palette: {
      primary: {
        light: '#B79A6C',
        main: AUTH_ACCENT,
        dark: AUTH_ACCENT_DARK,
        contrastText: '#FFFFFF',
      },
    },
    components: {
      // The rounded field is the auth look; it used to come from five
      // copies of an unscoped stylesheet that leaked into the console.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 40,
          },
        },
      },
    },
  })

  return (
    <ThemeProvider theme={scoped}>{children ?? <Outlet />}</ThemeProvider>
  )
}

export default AuthTheme
